/**
 * A self-contained pool search box: type part of a pool's name or its Pool
 * ID, see matching pools. Calls `POST /pool/search` (ISSUE-093) through the
 * generated query hook and renders exactly what the server returns.
 *
 * server-resolves-client-renders.mdc: the server already applied the query,
 * the filters, and the visibility rule (a private pool the fan cannot see
 * never reaches this component at all). This component never re-filters,
 * re-sorts, or hides a row itself.
 *
 * The route owns the query, not this pane. `url-state.mdc` rule 3 puts search
 * text in the URL, so `routes/pools_.search.tsx` holds it in `?q=` and hands
 * it down as a plain prop, exactly as `/pools` does for `PoolsListPane`. A
 * refresh, the browser back button and a pasted link all keep the search that
 * way, which a `useState` inside this pane could not do. The sport, league and
 * pool type filters (ISSUE-199 R4) are held the same way, in `?sportKey=`,
 * `?leagueKey=` and `?contestType=`, so a narrowed search is just as
 * shareable as an unnarrowed one.
 *
 * ISSUE-093 shipped the component, the index and the endpoint; ISSUE-199
 * mounted it, and `routes/pools_.search.tsx` is its render site. Keep it that
 * way: a pane with no render site is not shipped, whatever its own tests say.
 */
import {
  usePoolCreateOptions,
  usePoolSearch,
} from "@sportsmet/pool-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Card, InlineBanner, Input, Select, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useMemo } from "react";

import { PoolCardContent } from "./PoolCardContent";

import {
  choosePoolSearchLeague,
  choosePoolSearchSport,
  choosePoolSearchType,
  filterValueToKey,
  POOL_SEARCH_FILTER_COPY,
  poolSearchLeagueOptions,
  poolSearchSportOptions,
  poolSearchTypeOptions,
  type typePoolSearchFilters,
} from "./poolSearchFilters";
import {
  POOL_SEARCH_MIN_QUERY_LENGTH,
  resolvePoolSearchState,
  type typePoolSearchResultModel,
} from "./poolSearchState";

export type typePoolSearchPaneProps = {
  /** The `?q=` value, or "" when the param is absent. */
  query: string;
  onQueryChange: (query: string) => void;
  /**
   * The sport, league and pool type the fan narrowed by (ISSUE-199 R4), each
   * `null` when its param is absent. They live in the URL beside `?q=` for the
   * same reason the query does, so a filtered search is shareable and survives
   * a reload.
   */
  filters: typePoolSearchFilters;
  /**
   * Receives the whole filter set rather than one key at a time, because
   * choosing a sport also clears the league (`choosePoolSearchSport`). A
   * per-key handler would leave that rule for the caller to re-derive, and a
   * caller that forgot would send a sport and a league from two different
   * sports, which the server ANDs into zero rows.
   */
  onFiltersChange: (filters: typePoolSearchFilters) => void;
  /**
   * Receives the whole result model, not a bare pool id, because where a row
   * leads depends on the row: a pool the fan holds an invitation to opens the
   * invitation, never the pool (ISSUE-199 R5). The caller cannot make that
   * call from an id alone, and a pane that handed one over would have made
   * every invited row open a pool the fan is not a member of.
   */
  onSelectPool?: (result: typePoolSearchResultModel) => void;
};

const PoolSearchResultRowBody = ({
  model,
}: {
  model: typePoolSearchResultModel;
}): ReactNode => (
  <PoolCardContent
    name={model.name}
    contestType=""
    sport={model.sportLabel}
    facts={[model.sportLabel, model.leagueLabel]}
  >
    {model.isPrivate ? (
      <span
        className="text-caption text-ink-tertiary"
        data-testid={`pool-search-private-${model.poolId}`}
      >
        Private
      </span>
    ) : null}
    {model.hasPendingInvite ? (
      <span
        className="text-caption text-info"
        data-testid={`pool-search-invited-${model.poolId}`}
      >
        Invited
      </span>
    ) : null}
  </PoolCardContent>
);

// Card's own props are a discriminated union (ui-patterns.md): pass
// `onClick` and it renders as a real interactive `<button>`, reachable by
// keyboard and announced as clickable; omit it entirely and it renders as a
// plain, non-interactive `<div>`. `onSelectPool` is optional on this pane
// (a caller with nowhere to route a selection yet still wants a working
// search box), so the two Card shapes are rendered as two branches rather
// than passing `onClick={fn | undefined}`, which the union does not accept.
const PoolSearchResultRow = ({
  model,
  onSelect,
}: {
  model: typePoolSearchResultModel;
  onSelect?: (result: typePoolSearchResultModel) => void;
}): ReactNode =>
  onSelect ? (
    <Card
      aria-label={model.name}
      data-testid={`pool-search-result-${model.poolId}`}
      padding="default"
      onClick={() => {
        onSelect(model);
      }}
    >
      <PoolSearchResultRowBody model={model} />
    </Card>
  ) : (
    <Card
      aria-label={model.name}
      data-testid={`pool-search-result-${model.poolId}`}
      padding="default"
    >
      <PoolSearchResultRowBody model={model} />
    </Card>
  );

export const PoolSearchPane = ({
  query,
  onQueryChange,
  filters,
  onFiltersChange,
  onSelectPool,
}: typePoolSearchPaneProps): ReactNode => {
  const trimmedQuery = query.trim();

  // The same two reads `CreatePoolDialog` makes for its own league step. They
  // are the only source of the filter option lists: hardcoding sports, leagues
  // or pool types here would put a league in front of a fan that the catalog
  // has retired.
  const catalog = useLeagueCatalogGet({});
  const options = usePoolCreateOptions({});

  const sportItems = useMemo(
    () =>
      poolSearchSportOptions(
        catalog.data?.data?.sports ?? null,
        POOL_SEARCH_FILTER_COPY.allOption,
      ),
    [catalog.data],
  );
  const leagueItems = useMemo(
    () =>
      poolSearchLeagueOptions(
        catalog.data?.data?.sports ?? null,
        filters.sportKey,
        POOL_SEARCH_FILTER_COPY.allOption,
      ),
    [catalog.data, filters.sportKey],
  );
  const typeItems = useMemo(
    () =>
      poolSearchTypeOptions(
        options.data?.data?.contestTypes ?? [],
        POOL_SEARCH_FILTER_COPY.allOption,
      ),
    [options.data],
  );

  const search = usePoolSearch(
    {
      query: trimmedQuery,
      sportKey: filters.sportKey,
      leagueKey: filters.leagueKey,
      // `poolSearchFilters.ts` types contestType as `string | null` so it
      // stays free of the generated SDK's literal union. The value can only
      // have come from the server's own `contestTypes` rows, so this narrows a
      // value already known to be one of them rather than widening a guess.
      // Same reasoning as `apps/mobile/app/(tabs)/pools/search.tsx`.
      contestType: filters.contestType as "survivor" | null,
    },
    { enabled: trimmedQuery.length >= POOL_SEARCH_MIN_QUERY_LENGTH },
  );

  const state = resolvePoolSearchState({
    trimmedQuery,
    isLoading: search.isLoading,
    hasFailed: search.isError || Boolean(search.data?.error),
    items: search.data?.data?.items ?? null,
  });

  return (
    <div className="flex flex-col gap-3" data-testid="pool-search-pane">
      <Input
        type="search"
        label="Search pools"
        labelHidden
        placeholder="Search by name or Pool ID"
        value={query}
        onChange={(event) => {
          onQueryChange(event.target.value);
        }}
        data-testid="pool-search-input"
      />

      {/* R4. The server does the narrowing: each chosen key rides along in the
          `/pool/search` body and `searchPoolsPage` puts it on the Mongo
          filter. `Select` takes no `data-testid` of its own, so each wrapper
          carries one, the same way the failed banner's does. */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1" data-testid="pool-search-filter-sport">
          <Select
            label={POOL_SEARCH_FILTER_COPY.sportLabel}
            items={sportItems}
            value={filterValueToKey(filters.sportKey)}
            onValueChange={(next) => {
              onFiltersChange(choosePoolSearchSport(filters, next));
            }}
          />
        </div>
        {leagueItems.length > 0 ? (
          <div className="flex-1" data-testid="pool-search-filter-league">
            <Select
              label={POOL_SEARCH_FILTER_COPY.leagueLabel}
              items={leagueItems}
              value={filterValueToKey(filters.leagueKey)}
              onValueChange={(next) => {
                onFiltersChange(choosePoolSearchLeague(filters, next));
              }}
            />
          </div>
        ) : null}
        <div className="flex-1" data-testid="pool-search-filter-type">
          <Select
            label={POOL_SEARCH_FILTER_COPY.typeLabel}
            items={typeItems}
            value={filterValueToKey(filters.contestType)}
            onValueChange={(next) => {
              onFiltersChange(choosePoolSearchType(filters, next));
            }}
          />
        </div>
      </div>

      {state.phase === "idle" ? (
        <p
          className="text-caption text-ink-secondary"
          data-testid="pool-search-idle"
        >
          Type a pool name or Pool ID to search.
        </p>
      ) : null}

      {state.phase === "loading" ? (
        <div className="flex flex-col gap-3" data-testid="pool-search-loading">
          <Skeleton shape="card" label="Searching pools" />
          <Skeleton shape="card" />
        </div>
      ) : null}

      {state.phase === "failed" ? (
        // InlineBanner hardcodes its own data-testid="inline-banner" and
        // does not forward one passed in (the same trait Skeleton has), so
        // this wrapper carries the phase's own testid instead.
        <div data-testid="pool-search-failed">
          <InlineBanner
            variant="warning"
            message="We could not search pools."
            action={{
              label: "Try again",
              onAction: () => {
                void search.refetch();
              },
            }}
          />
        </div>
      ) : null}

      {state.phase === "noResults" ? (
        <div
          className="flex flex-col items-center gap-2 py-10 text-center"
          data-testid="pool-search-no-results"
        >
          <p className="text-body-strong text-ink">{`No pools match "${state.query}"`}</p>
          <p className="text-body text-ink-secondary">Try a different name.</p>
        </div>
      ) : null}

      {state.phase === "ready" ? (
        <div className="flex flex-col gap-3" data-testid="pool-search-results">
          {state.results.map((result) => (
            <PoolSearchResultRow
              key={result.poolId}
              model={result}
              {...(onSelectPool ? { onSelect: onSelectPool } : {})}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};
